import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from './api';

/**
 * The per-user Outlook connector and signatures (spec 0014), shared by My email settings, Org
 * Settings → Email, the template email modal and the activity composer.
 *
 * appealstack never sends a user's email: it creates a draft in their own Outlook Drafts
 * folder and they send it from Outlook.
 */

export interface MailConnection {
  id: string;
  provider: 'microsoft';
  account_email: string | null;
  status: 'connected' | 'error' | 'revoked';
  connected_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
}

export interface MailConnectionState {
  configured: boolean;
  providers: Array<{ provider: string; label: string; configured: boolean }>;
  connection: MailConnection | null;
}

export interface UserSignature {
  id: string;
  name: string;
  body_html: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

export const MAIL_CONNECTION_KEY = ['mail-connections', 'me'] as const;
export const SIGNATURES_KEY = ['user-signatures'] as const;

export function useMailConnection(enabled = true) {
  return useQuery<MailConnectionState>({
    queryKey: MAIL_CONNECTION_KEY,
    queryFn: ({ signal }) => api.get<MailConnectionState>('/api/mail-connections/me', signal),
    enabled,
    retry: false,
  });
}

export function useSignatures(enabled = true) {
  return useQuery<UserSignature[]>({
    queryKey: SIGNATURES_KEY,
    queryFn: ({ signal }) => api.get<UserSignature[]>('/api/users/me/signatures', signal),
    enabled,
    retry: false,
  });
}

/** True only for a live, verified link. */
export function isConnected(state: MailConnectionState | undefined): boolean {
  return !!state?.connection && state.connection.status === 'connected';
}

const RETURN_KEY = 'appealstack.mail.returnTo';

/**
 * Start Microsoft's consent flow for the signed-in user. The browser leaves for Microsoft and
 * comes back to /account/email; `returnTo` (the page the user was composing on) is remembered so
 * that page can send them straight back, where their saved draft is waiting.
 */
export async function startOutlookConnect(returnTo?: string): Promise<void> {
  const { authorize_url: url } = await api.post<{ authorize_url: string }>('/api/mail-connections/microsoft/start', {});
  try {
    if (returnTo) window.sessionStorage.setItem(RETURN_KEY, returnTo);
  } catch { /* storage unavailable: the user lands on My email settings instead */ }
  window.location.assign(url);
}

/** The page to go back to after connecting, once (then forgotten). */
export function takeConnectReturnPath(): string | null {
  try {
    const v = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null;
  } catch { return null; }
}

/** The human sentence for a callback outcome code (?mail_error=…). */
export function connectErrorText(code: string | null): string | null {
  switch (code) {
    case null: return null;
    case 'denied': return 'Microsoft sign-in was cancelled, so Outlook is not connected.';
    case 'state': return 'That sign-in link was started from a different session. Start Connect Outlook again from here.';
    case 'verify': return 'Microsoft signed you in but the mailbox could not be read, so it was not connected. Check that the account has an Exchange Online mailbox.';
    case 'unconfigured': return 'The Outlook connector is not set up for this firm yet.';
    default: return 'Outlook could not be connected. Try again, or ask your administrator whether your Microsoft 365 tenant has approved appealstack.';
  }
}

export function mailErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) return String((err.body as { error: unknown }).error);
  return err instanceof Error ? err.message : String(err);
}
